"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileText, Layers, SlidersHorizontal as Sliders, Sparkles, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyFeatures = [{
  title: "Inline & At-Line PAT",
  description: "Continuous real-time measurement in blend, dry, and fluid beds."
}, {
  title: "Multiplex Fiber Channels",
  description: "Up to 9 optical probe channels with zero moving parts."
}, {
  title: "Regulatory-Ready Design",
  description: "Comprehensive validation documentation and audit trail support."
}];
const technicalSpecs = [{
  label: "Spectral Range",
  value: "950 – 2150 nm"
}, {
  label: "Scan Speed",
  value: "< 100 ms / scan"
}, {
  label: "Enclosure",
  value: "IP65 / ATEX Zone 0/20"
}, {
  label: "Connectivity",
  value: "OPC-UA / Modbus / Ethernet"
}];
export default function SpectrometerHero() {
  return <section id="nirprocessspectrometerhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Subtle Background Pattern */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.08),transparent_50%)]" />

      <div className="page-container relative z-10">
        {/* Breadcrumb / Category Tag */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <Badge variant="secondary" className="px-3 py-1 font-medium text-xs">
            Process Analytical Technology (PAT)
          </Badge>
          <span className="text-muted-foreground text-xs">/</span>
          <span className="text-xs font-medium text-muted-foreground">
            NIR Process Spectrometer Series
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Main Hero Copy Column */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary border border-border w-fit text-xs font-semibold text-secondary-foreground">
              <Sparkles className="size-3.5 text-primary" />
              <span>Engineered for Regulated Manufacturing</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
              Real-Time NIR Process Spectrometers for High-Integrity Lines
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground font-default max-w-2xl leading-relaxed">
              Continuous chemical and physical composition monitoring directly in your process stream. 
              Eliminate hold times in drying, blending, and granulation with robust, non-destructive optical spectroscopy.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="h-11 px-6">
                <Link to="/ContactPage">
                  <span>Speak with an Application Specialist</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-11 px-6">
                <Link to="/ProcessAnalyticalTechnologyPage">
                  <FileText className="size-4 text-primary" />
                  <span>Explore PAT Solutions</span>
                </Link>
              </Button>
            </div>

            <Separator className="my-2" />

            {/* Capability Feature Bullets */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              {keyFeatures.map((feature, i) => <div key={feature.title} data-index={i} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="font-semibold text-sm text-foreground">
                      {feature.title}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {feature.description}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Product Overview Showcase Card */}
          <div className="lg:col-span-5">
            <Card className="border-border bg-card shadow-md relative overflow-hidden">
              {/* Status Ribbon */}
              <div className="p-5 bg-secondary/50 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
                  </span>
                  <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Model NIR-Pro 4000
                  </span>
                </div>
                <Badge variant="outline" className="text-xs font-mono">
                  Online Telemetry Ready
                </Badge>
              </div>

              {/* Product Visual Area */}
              <div className="relative p-6 flex flex-col items-center justify-center bg-muted/30">
                <div className="w-full h-48 sm:h-56 rounded-lg bg-card border border-border/70 p-4 flex flex-col justify-between relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Cpu className="size-4 text-primary" />
                      <span className="text-xs font-medium text-foreground">Diode Array Engine</span>
                    </div>
                    <span className="text-[11px] font-mono bg-secondary px-2 py-0.5 rounded text-secondary-foreground border border-border">
                      RMS Noise &lt; 10 µAU
                    </span>
                  </div>

                  {/* Simulated Spectrum Line */}
                  <div className="my-auto py-2">
                    <div className="flex items-end justify-between gap-1 h-16 px-2">
                      {[25, 38, 42, 68, 85, 92, 70, 54, 40, 60, 78, 95, 62, 45, 30, 20].map((h, i) => <div key={i} data-index={i} className="w-full bg-primary/25 rounded-t-sm transition-all duration-300 hover:bg-primary" style={{
                      height: `${h}%`
                    }} />)}
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground font-mono mt-1 px-2 border-t border-border pt-1">
                      <span>1100 nm</span>
                      <span>1650 nm</span>
                      <span>2100 nm</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Activity className="size-3.5 text-primary" />
                      <span>Live Absorbance Stream</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Layers className="size-3.5 text-muted-foreground" />
                      <span>4 Active Probes</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Technical Specifications Grid */}
              <CardContent className="p-6 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Core Specifications
                  </span>
                  <Link to="/PowderTestingPage" className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
                    <span>View application test</span>
                    <ArrowRight className="size-3" />
                  </Link>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  {technicalSpecs.map((spec, i) => <div key={spec.label} data-index={i} className="p-2.5 rounded-md bg-secondary/40 border border-border flex flex-col gap-0.5">
                      <span className="text-muted-foreground text-[11px]">{spec.label}</span>
                      <span className="font-semibold text-foreground">{spec.value}</span>
                    </div>)}
                </div>

                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Sliders className="size-3.5 text-primary" />
                    <span>Turnkey calibration models included</span>
                  </div>
                  <Button asChild variant="ghost" size="sm" className="h-7 text-xs px-2">
                    <Link to="/ContactPage">
                      <span>Request Quote</span>
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}